import env from '../config/env';
import type { AnalyticsEventPayload } from './events';
import { getBearerToken, getCachedBearerToken } from '../auth/runtimeAuth';

const INGEST_PATH = '/api/analytics/ingest';

const getIngestUrl = (): string => {
  const base = env.API_BASE_URL || '';
  return `${base}${INGEST_PATH}`;
};

/**
 * Whether a batch that failed to land is worth sending again.
 *
 * Only transport-level and server-side failures are: those are transient, which
 * is the whole reason the queue persists. A 4xx means the server looked at this
 * batch and rejected it, so retrying re-sends the same rejected bytes every 5s
 * for as long as the tab is open. Drop it instead — losing a few observational
 * events beats a permanent request loop.
 *
 * Exported for tests.
 */
export const isRetryableStatus = (status: number): boolean => !(status >= 400 && status < 500);

export const sendBatchFetch = async (
  events: AnalyticsEventPayload[]
): Promise<{ ok: boolean; retryable: boolean }> => {
  if (events.length === 0) return { ok: true, retryable: false };
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    const token = await getBearerToken();
    if (token) headers.Authorization = `Bearer ${token}`;
    const response = await fetch(getIngestUrl(), {
      method: 'POST',
      headers,
      body: JSON.stringify({ events }),
      credentials: 'include',
      keepalive: false,
    });
    if (response.ok) return { ok: true, retryable: false };
    return { ok: false, retryable: isRetryableStatus(response.status) };
  } catch {
    // Network-level failure — offline, DNS, aborted. Always worth another go.
    return { ok: false, retryable: true };
  }
};

export const sendBatchBeacon = (events: AnalyticsEventPayload[]): boolean => {
  if (events.length === 0) return true;
  try {
    if (typeof navigator === 'undefined' || typeof navigator.sendBeacon !== 'function') return false;
    const token = getCachedBearerToken();
    const body: { events: AnalyticsEventPayload[]; authToken?: string } = { events };
    if (token) body.authToken = token;
    const blob = new Blob([JSON.stringify(body)], { type: 'application/json' });
    return navigator.sendBeacon(getIngestUrl(), blob);
  } catch {
    return false;
  }
};
